import { useState } from 'react';
import { FieldValues, useController } from 'react-hook-form';
import { Field, FieldProps as SWFieldProps } from '@spark-web/field';
import { FloatInput, FloatInputProps } from '@spark-web/float-input';
import { FormFieldProps } from '../Form';

export type FloatInputFieldProps<TFieldValues extends FieldValues = FieldValues> = FormFieldProps<TFieldValues> &
  Omit<FloatInputProps, 'onChange'> & {
    FieldProps?: Omit<SWFieldProps, 'label' | 'children'>;
    onChange?: (value: number) => void;
  };

function FloatInputField<TFieldValues extends FieldValues = FieldValues>({
  name,
  label,
  control,
  shouldUnregister,
  FieldProps,
  ...floatInputProps
}: FloatInputFieldProps<TFieldValues>) {
  const {
    field: { value, onChange, ...field },
    fieldState: { error },
  } = useController<TFieldValues>({
    name,
    control,
    rules: {
      onBlur: floatInputProps.onBlur,
    },
    shouldUnregister,
  });

  // * On initial render, if the underlying input value is falsy, the input should display nothing
  const [isPristine, setIsPristine] = useState(true);

  const handleChange = (newValue: string | number) => {
    if (isPristine) {
      setIsPristine(false);
    }

    const floatValue = typeof newValue === 'string' ? 0 : newValue;

    onChange(floatValue);

    floatInputProps.onChange?.(floatValue);
  };

  return (
    <Field
      label={label}
      {...FieldProps}
      tone={error ? 'critical' : FieldProps?.tone}
      message={error?.message ?? FieldProps?.message}
    >
      <FloatInput {...floatInputProps} {...field} value={isPristine && !value ? '' : value} onChange={handleChange} />
    </Field>
  );
}

export default FloatInputField;
